/**
 * Everything a signed-in session needs before a page renders, in order:
 *   1. signed in (or sent to sign in) — the shared RequireAuth over the generated auth provider:
 *      Entra redirect on hosted, email/password and MFA on self-hosted, as today;
 *   2. the API client pointed at this deployment and holding a live token;
 *   3. the terms accepted;
 *   4. which workspace this session is in;
 *   5. one live event stream for the session.
 */
import { useEffect, useMemo, useRef } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { RequireAuth, StateBlock } from '@inferrex/ui';
import { useAuth } from '@inferrex/ui/auth';
import {
  eventStreamUrl, initApiClient, mintEventStreamTicket, setTokenProvider, updateApiToken,
} from '@inferrex/api-client';
import { apiBaseUrl } from '../lib/runtime-config';
import { EventStream } from '../lib/event-stream';
import { LiveEventsProvider } from '../lib/live-events';
import { TermsGate } from './TermsGate';
import { WorkspaceEntry } from './WorkspaceEntry';

const Loading = () => <div className="app-spinner"><StateBlock kind="loading" rows={2} message="Signing you in…" /></div>;

/** Point the shared API client at this deployment with the session's token, before any query runs. */
function useApiClient(): void {
  const { token, getToken } = useAuth();
  const initialised = useRef(false);
  // Synchronously on first render, so the first page's queries already have a client.
  if (!initialised.current) {
    initApiClient(token ?? '', apiBaseUrl());
    initialised.current = true;
  }
  useEffect(() => { if (token) updateApiToken(token); }, [token]);
  // Every request asks the identity provider for a current token rather than the one from sign-in.
  useEffect(() => {
    setTokenProvider(() => getToken());
    return () => setTokenProvider(null);
  }, [getToken]);
}

function SignedInSession() {
  useApiClient();
  const stream = useMemo(() => new EventStream({
    mintTicket: mintEventStreamTicket,
    streamUrl: eventStreamUrl,
    createSource: (url) => new EventSource(url),
  }), []);
  return (
    <TermsGate>
      <WorkspaceEntry>
        <LiveEventsProvider stream={stream}>
          <Outlet />
        </LiveEventsProvider>
      </WorkspaceEntry>
    </TermsGate>
  );
}

export function SessionLayout() {
  const { configError } = useAuth();
  // A misconfigured identity provider can leave a stale user in place; treat it as signed out,
  // so the sign-in page shows the configuration problem (behaviour as frontend/src/App.tsx).
  if (configError) return <Navigate to="/sign-in" replace />;
  return (
    <RequireAuth loginPath="/sign-in" loadingFallback={<Loading />}>
      <SignedInSession />
    </RequireAuth>
  );
}
